import {AutomationExecution} from 'crqlar-react-common/dist/models/automation/automation';
import {AutomationService} from 'crqlar-react-common/dist/services/automation/AutomationService';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useQuery} from 'react-query';

import {loadMemberRuns} from '../executions/memberRuns';
import {queuedChildren} from '../utils/executionSteps';
import {TestRunState, TestRunSubject, testRunState} from '../utils/testRunState';

const POLL_INTERVAL_MS = 1500;

// After this the test is "slow": polled less often, and the strip says it finishes on its own.
const SLOW_AFTER_MS = 60 * 1000;
const SLOW_POLL_INTERVAL_MS = 5000;

// A parked test is still polled, so a short Wait plays out on screen.
const PARKED_POLL_INTERVAL_MS = 15000;

/** The test's own run, then its member runs level by level (members may fan out again); never other runs of the flow. */
export async function loadTestRunExecutions(rootId: string): Promise<AutomationExecution[]> {
    const root = await AutomationService.getExecution(rootId);
    const executions = [root];

    let parents = [root];
    while (parents.length) {
        const next: AutomationExecution[] = [];
        for (const parent of parents) {
            if (parent.id && queuedChildren(parent)) next.push(...await loadMemberRuns(parent.id));
        }
        executions.push(...next);
        parents = next;
    }

    return executions;
}

export interface WatchedTestRun {
    rootId: string;
    subject: TestRunSubject;
    state: TestRunState;
    /** Still running after a minute: the strip says it finishes on its own and offers View run. */
    slow: boolean;
}

/**
 * Follows the test run the header started, never an automatic run; polls until every run settles. `close`
 * only stops watching (the run goes on). The API accepts `skipWait` only for a test run's timed waits.
 */
export function useTestRunWatch() {
    const [watched, setWatched] = useState<{rootId: string; subject: TestRunSubject; startedAt: number} | null>(null);
    const [slow, setSlow] = useState(false);
    const [skip, setSkip] = useState<'idle' | 'skipping' | 'failed'>('idle');

    const watch = useCallback((rootId: string, subject: TestRunSubject) => {
        setWatched({rootId, subject, startedAt: Date.now()});
        setSlow(false);
        setSkip('idle');
    }, []);

    const close = useCallback(() => setWatched(null), []);

    const {data: executions, refetch} = useQuery(
        ['automation-test-run', watched?.rootId],
        () => loadTestRunExecutions(watched!.rootId),
        {
            enabled: !!watched,
            refetchOnWindowFocus: false,
            retry: false,
            cacheTime: 0
        }
    );

    const subject = watched?.subject;
    const state = useMemo(
        () => testRunState(executions, subject ?? {address: ''}, Date.now()),
        [executions, subject]
    );
    const phase = state.phase;

    const interval = !watched
        ? false
        : phase === 'running' ? (slow ? SLOW_POLL_INTERVAL_MS : POLL_INTERVAL_MS)
            : phase === 'parked' ? PARKED_POLL_INTERVAL_MS : false;

    useEffect(() => {
        if (!interval) return;
        const timer = window.setInterval(() => void refetch(), interval);
        return () => window.clearInterval(timer);
    }, [interval, refetch]);

    useEffect(() => {
        if (!watched || phase !== 'running' || slow) return;
        const timer = window.setTimeout(() => setSlow(true), Math.max(0, SLOW_AFTER_MS - (Date.now() - watched.startedAt)));
        return () => window.clearTimeout(timer);
    }, [watched, phase, slow]);

    const skipWait = useCallback(async () => {
        if (!watched) return;
        setSkip('skipping');
        try {
            await AutomationService.skipTestWait(watched.rootId);
            // The test runs again, so the minute before it counts as slow starts over.
            setWatched(current => current && {...current, startedAt: Date.now()});
            setSlow(false);
            await refetch();
            setSkip('idle');
        } catch {
            setSkip('failed');
        }
    }, [watched, refetch]);

    const run: WatchedTestRun | null = watched && {rootId: watched.rootId, subject: watched.subject, state, slow};

    return {
        run,
        /** The test is under way and not parked — the header's Test run button waits for it. */
        busy: !!watched && phase === 'running',
        watch,
        close,
        skipWait,
        skipping: skip === 'skipping',
        skipFailed: skip === 'failed'
    };
}
